import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./Content";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
); 

const MaxBuyCalculator = () => {
  const content = article.find((item) => item.id === 8);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [calculationMode, setCalculationMode] = useState("budget"); // budget, margin, risk

  const [inputs, setInputs] = useState({
    // Budget-based inputs
    availableCash: "5000",
    stockPrice: "50.00",
    maxBudgetPercent: "100",
    
    // Margin-based inputs
    accountEquity: "25000",
    marginRequirement: "50",
    currentMarginUsed: "0",
    
    // Risk-based inputs
    riskCapital: "10000",
    maxRiskPercent: "2",
    stopLossPrice: "45.00",
    
    // Advanced inputs
    commission: "10.00",
    fees: "5.00",
    taxes: "15.00",
    
    // Portfolio constraints
    maxPositionPercent: "20",
    portfolioValue: "50000",
    sectorExposure: "15",
    
    // Market constraints
    dailyBuyingPower: "10000",
    priceVolatility: "15",
    marketCondition: "normal", // bullish, normal, bearish
    
    // Common inputs
    currency: "1",
    leverage: "1"
  });

  const [constraints, setConstraints] = useState([
    { type: "budget", value: "5000", active: true },
    { type: "risk", value: "200", active: true },
    { type: "portfolio", value: "20", active: true },
    { type: "margin", value: "12500", active: false }
  ]);

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateMaxBuy();
    }
  }, [inputs, constraints, calculationMode, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handleConstraintChange = (index, field, value) => {
    const updatedConstraints = constraints.map((constraint, i) => 
      i === index ? { ...constraint, [field]: value } : constraint
    );
    setConstraints(updatedConstraints);
  };

  const toggleConstraint = (index) => {
    const updatedConstraints = constraints.map((constraint, i) => 
      i === index ? { ...constraint, active: !constraint.active } : constraint
    );
    setConstraints(updatedConstraints);
  };

  const addConstraint = () => {
    setConstraints([...constraints, { type: "custom", value: "", active: true }]);
  };

  const removeConstraint = (index) => {
    if (constraints.length > 1) {
      setConstraints(constraints.filter((_, i) => i !== index));
    }
  };

  const calculateMaxBuy = () => {
    const currencyRate = parseFloat(inputs.currency) || 1;
    const stockPrice = parseFloat(inputs.stockPrice) || 0;
    const commission = parseFloat(inputs.commission) || 0;
    const fees = parseFloat(inputs.fees) || 0;
    const taxes = parseFloat(inputs.taxes) || 0;
    const leverage = parseFloat(inputs.leverage) || 1;

    let maxShares = 0;
    let maxInvestment = 0;
    let limitingFactor = "";
    let calculationDetails = [];

    // Calculate based on different methods
    const calculations = {
      budget: calculateBudgetBased(),
      margin: calculateMarginBased(),
      risk: calculateRiskBased()
    };

    // Apply active constraints
    const constrainedCalculations = applyConstraints(calculations);

    // Find the most limiting calculation
    const activeCalculations = Object.values(constrainedCalculations).filter(calc => calc.active);
    
    if (activeCalculations.length > 0) {
      const mostLimiting = activeCalculations.reduce((min, calc) => 
        calc.maxShares < min.maxShares ? calc : min
      );

      maxShares = Math.floor(mostLimiting.maxShares);
      maxInvestment = mostLimiting.maxInvestment;
      limitingFactor = mostLimiting.factor;
      calculationDetails = activeCalculations;
    }

    // Calculate cost breakdown
    const totalCost = maxShares * stockPrice;
    const totalFees = commission + fees;
    const estimatedTaxes = (taxes / 100) * totalCost;
    const totalInvestment = totalCost + totalFees + estimatedTaxes;

    // Calculate leverage impact
    const leveragedShares = Math.floor(maxShares * leverage);
    const leveragedInvestment = totalInvestment * leverage;

    // Risk metrics
    const positionSizePercent = (totalInvestment / (parseFloat(inputs.portfolioValue) || totalInvestment)) * 100;
    const riskAmount = parseFloat(inputs.maxRiskPercent) / 100 * parseFloat(inputs.riskCapital) || 0;

    // Buying power analysis
    const remainingCash = parseFloat(inputs.availableCash) - totalInvestment;
    const remainingBuyingPower = parseFloat(inputs.dailyBuyingPower) - totalInvestment;

    const resultData = {
      maxShares,
      leveragedShares,
      maxInvestment: maxInvestment * currencyRate,
      leveragedInvestment: leveragedInvestment * currencyRate,
      totalCost: totalCost * currencyRate,
      totalFees: totalFees * currencyRate,
      estimatedTaxes: estimatedTaxes * currencyRate,
      totalInvestment: totalInvestment * currencyRate,
      stockPrice: stockPrice * currencyRate,
      limitingFactor,
      calculationDetails,
      currencyRate,
      leverage,
      
      // Risk metrics
      positionSizePercent,
      riskAmount: riskAmount * currencyRate,
      
      // Remaining capacity
      remainingCash: Math.max(0, remainingCash) * currencyRate,
      remainingBuyingPower: Math.max(0, remainingBuyingPower) * currencyRate,
      cashUtilization: parseFloat(inputs.availableCash) > 0 ? 
        (totalInvestment / parseFloat(inputs.availableCash)) * 100 : 0,
      
      // Constraints analysis
      activeConstraints: constraints.filter(c => c.active).length,
      appliedConstraints: calculationDetails.length
    };

    setResult(resultData);
  };

  const calculateBudgetBased = () => {
    const availableCash = parseFloat(inputs.availableCash) || 0;
    const stockPrice = parseFloat(inputs.stockPrice) || 0;
    const maxBudgetPercent = parseFloat(inputs.maxBudgetPercent) || 100;
    const commission = parseFloat(inputs.commission) || 0;
    const fees = parseFloat(inputs.fees) || 0;

    const maxBudget = (maxBudgetPercent / 100) * availableCash;
    const availableAfterFees = Math.max(0, maxBudget - commission - fees);
    
    let maxShares = 0;
    if (stockPrice > 0) {
      maxShares = Math.floor(availableAfterFees / stockPrice);
    }

    return {
      type: "budget",
      factor: "Available Cash",
      maxShares,
      maxInvestment: maxShares * stockPrice,
      limit: maxBudget,
      active: calculationMode === "budget" || constraints.find(c => c.type === "budget" && c.active)
    };
  };

  const calculateMarginBased = () => {
    const accountEquity = parseFloat(inputs.accountEquity) || 0;
    const marginRequirement = parseFloat(inputs.marginRequirement) || 50;
    const currentMarginUsed = parseFloat(inputs.currentMarginUsed) || 0;
    const stockPrice = parseFloat(inputs.stockPrice) || 0;
    const commission = parseFloat(inputs.commission) || 0;
    const fees = parseFloat(inputs.fees) || 0;

    const availableMargin = accountEquity * (marginRequirement / 100) - currentMarginUsed;
    const availableAfterFees = Math.max(0, availableMargin - commission - fees);
    
    let maxShares = 0;
    if (stockPrice > 0) {
      maxShares = Math.floor(availableAfterFees / stockPrice);
    }

    return {
      type: "margin",
      factor: "Margin Capacity",
      maxShares,
      maxInvestment: maxShares * stockPrice,
      limit: availableMargin,
      active: calculationMode === "margin" || constraints.find(c => c.type === "margin" && c.active)
    };
  };

  const calculateRiskBased = () => {
    const riskCapital = parseFloat(inputs.riskCapital) || 0;
    const maxRiskPercent = parseFloat(inputs.maxRiskPercent) || 0;
    const stockPrice = parseFloat(inputs.stockPrice) || 0;
    const stopLossPrice = parseFloat(inputs.stopLossPrice) || 0;
    const commission = parseFloat(inputs.commission) || 0;
    const fees = parseFloat(inputs.fees) || 0;

    const riskAmount = (maxRiskPercent / 100) * riskCapital;
    const riskPerShare = stockPrice - stopLossPrice;
    
    let maxShares = 0;
    if (riskPerShare > 0) {
      maxShares = Math.floor(riskAmount / riskPerShare);
    }

    // Adjust for fees
    const sharesValue = maxShares * stockPrice;
    const totalCost = sharesValue + commission + fees;
    const adjustedShares = Math.floor((riskAmount - commission - fees) / riskPerShare);

    return {
      type: "risk",
      factor: "Risk Management",
      maxShares: Math.min(maxShares, adjustedShares),
      maxInvestment: Math.min(maxShares, adjustedShares) * stockPrice,
      limit: riskAmount,
      active: calculationMode === "risk" || constraints.find(c => c.type === "risk" && c.active)
    };
  };

  const applyConstraints = (calculations) => {
    const activeConstraints = constraints.filter(c => c.active);
    
    return Object.keys(calculations).reduce((constrained, key) => {
      const calculation = calculations[key];
      
      if (calculation.active) {
        // Apply portfolio constraint
        const portfolioConstraint = activeConstraints.find(c => c.type === "portfolio");
        if (portfolioConstraint) {
          const portfolioValue = parseFloat(inputs.portfolioValue) || 0;
          const maxPortfolioPercent = parseFloat(portfolioConstraint.value) || 0;
          const maxFromPortfolio = (maxPortfolioPercent / 100) * portfolioValue;
          const maxSharesFromPortfolio = Math.floor(maxFromPortfolio / calculation.maxInvestment * calculation.maxShares);
          
          calculation.maxShares = Math.min(calculation.maxShares, maxSharesFromPortfolio);
          calculation.maxInvestment = calculation.maxShares * parseFloat(inputs.stockPrice);
        }

        // Apply other value constraints
        activeConstraints.forEach(constraint => {
          if (constraint.type !== "portfolio") {
            const constraintValue = parseFloat(constraint.value) || 0;
            if (calculation.maxInvestment > constraintValue) {
              const maxSharesFromConstraint = Math.floor(constraintValue / parseFloat(inputs.stockPrice));
              calculation.maxShares = Math.min(calculation.maxShares, maxSharesFromConstraint);
              calculation.maxInvestment = calculation.maxShares * parseFloat(inputs.stockPrice);
              calculation.factor = `Constraint: ${constraint.type}`;
            }
          }
        });
      }
      
      constrained[key] = calculation;
      return constrained;
    }, {});
  };

  const calculateScenarioAnalysis = () => {
    if (!result) return [];

    const scenarios = [
      { priceChange: -30, label: "Severe Drop", probability: 10 },
      { priceChange: -15, label: "Major Drop", probability: 20 },
      { priceChange: -5, label: "Moderate Drop", probability: 30 },
      { priceChange: 0, label: "Current", probability: 50 },
      { priceChange: 10, label: "Moderate Gain", probability: 60 },
      { priceChange: 25, label: "Major Gain", probability: 30 },
      { priceChange: 50, label: "Substantial Gain", probability: 15 }
    ];

    return scenarios.map(scenario => {
      const priceChange = scenario.priceChange / 100;
      const newPrice = result.stockPrice * (1 + priceChange);
      const profitLoss = (newPrice - result.stockPrice) * result.maxShares;
      const percentChange = (profitLoss / result.maxInvestment) * 100;
      const expectedValue = (profitLoss * scenario.probability) / 100;
      
      return {
        ...scenario,
        newPrice,
        profitLoss,
        percentChange,
        expectedValue,
        isProfit: profitLoss >= 0
      };
    });
  };

  const calculatePriceLevels = () => {
    if (!result) return [];

    const levels = [
      { price: result.stockPrice * 0.5, type: "extreme_support", label: "Extreme Support" },
      { price: result.stockPrice * 0.7, type: "strong_support", label: "Strong Support" },
      { price: result.stockPrice * 0.85, type: "moderate_support", label: "Moderate Support" },
      { price: result.stockPrice, type: "current", label: "Current Price" },
      { price: result.stockPrice * 1.15, type: "moderate_resistance", label: "Moderate Resistance" },
      { price: result.stockPrice * 1.3, type: "strong_resistance", label: "Strong Resistance" },
      { price: result.stockPrice * 1.5, type: "extreme_resistance", label: "Extreme Resistance" }
    ];

    return levels.map(level => {
      const sharesAffordable = Math.floor(result.maxInvestment / level.price);
      const investment = sharesAffordable * level.price;
      
      return {
        ...level,
        sharesAffordable,
        investment
      };
    });
  };

  const resetCalculator = () => {
    setConstraints([
      { type: "budget", value: "5000", active: true },
      { type: "risk", value: "200", active: true },
      { type: "portfolio", value: "20", active: true },
      { type: "margin", value: "12500", active: false }
    ]);
    setInputs({
      availableCash: "5000",
      stockPrice: "50.00",
      maxBudgetPercent: "100",
      accountEquity: "25000",
      marginRequirement: "50",
      currentMarginUsed: "0",
      riskCapital: "10000",
      maxRiskPercent: "2",
      stopLossPrice: "45.00",
      commission: "10.00",
      fees: "5.00",
      taxes: "15.00",
      maxPositionPercent: "20",
      portfolioValue: "50000",
      sectorExposure: "15",
      dailyBuyingPower: "10000",
      priceVolatility: "15",
      marketCondition: "normal",
      currency: "1",
      leverage: "1"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Maximum Shares,${result.maxShares}`,
      `With Leverage,${result.leveragedShares}`,
      `Total Investment,${result.totalInvestment.toFixed(2)}`,
      `Leveraged Investment,${result.leveragedInvestment.toFixed(2)}`,
      `Stock Price,${result.stockPrice.toFixed(2)}`,
      `Total Cost,${result.totalCost.toFixed(2)}`,
      `Fees & Commission,${result.totalFees.toFixed(2)}`,
      `Estimated Taxes,${result.estimatedTaxes.toFixed(2)}`,
      `Limiting Factor,${result.limitingFactor}`,
      `Position Size %,${result.positionSizePercent.toFixed(2)}%`,
      `Cash Utilization,${result.cashUtilization.toFixed(2)}%`,
      `Remaining Cash,${result.remainingCash.toFixed(2)}`,
      `Active Constraints,${result.activeConstraints}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'max-buy-calculation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      constraints: [...constraints],
      calculationMode,
      ...result
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setConstraints(calc.constraints || []);
    setCalculationMode(calc.calculationMode || "budget");
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        calculationMode: "budget",
        inputs: { 
          availableCash: "10000", 
          maxBudgetPercent: "50",
          maxRiskPercent: "1",
          leverage: "1"
        },
        constraints: [
          { type: "budget", value: "5000", active: true },
          { type: "risk", value: "100", active: true },
          { type: "portfolio", value: "10", active: true }
        ]
      },
      moderate: { 
        calculationMode: "risk",
        inputs: { 
          riskCapital: "20000", 
          maxRiskPercent: "2",
          leverage: "2",
          portfolioValue: "100000"
        },
        constraints: [
          { type: "budget", value: "10000", active: true },
          { type: "risk", value: "400", active: true },
          { type: "portfolio", value: "15", active: true }
        ]
      },
      aggressive: { 
        calculationMode: "margin",
        inputs: { 
          accountEquity: "50000", 
          marginRequirement: "50",
          leverage: "3",
          maxBudgetPercent: "100"
        },
        constraints: [
          { type: "budget", value: "25000", active: true },
          { type: "risk", value: "1000", active: true },
          { type: "portfolio", value: "25", active: true },
          { type: "margin", value: "25000", active: true }
        ]
      }
    };
    
    const presetData = presets[preset];
    setCalculationMode(presetData.calculationMode);
    setInputs(prev => ({ ...prev, ...presetData.inputs }));
    setConstraints(presetData.constraints);
    addNotification(`${preset} preset applied`, "info");
  };

  // Chart Data
  const investmentChartData = result ? {
    labels: ["Stock Cost", "Fees & Commission", "Estimated Taxes"],
    datasets: [{
      data: [result.totalCost, result.totalFees, result.estimatedTaxes],
      backgroundColor: ["#19692C", "#FFD93D", "#FF6B6B"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const scenarioData = calculateScenarioAnalysis();
  const scenarioChartData = scenarioData.length > 0 ? {
    labels: scenarioData.map(s => s.label),
    datasets: [
      {
        label: 'Profit/Loss',
        data: scenarioData.map(s => s.profitLoss),
        backgroundColor: scenarioData.map(s => s.isProfit ? '#28A745' : '#E53E3E'),
        borderColor: scenarioData.map(s => s.isProfit ? '#28A745' : '#E53E3E'),
        borderWidth: 2
      },
      {
        label: 'Expected Value',
        data: scenarioData.map(s => s.expectedValue),
        backgroundColor: '#FFD93D',
        borderColor: '#FFD93D',
        borderWidth: 2,
        type: 'line',
        yAxisID: 'y1'
      }
    ]
  } : null;

  const priceLevelsData = calculatePriceLevels();
  const priceLevelsChartData = priceLevelsData.length > 0 ? {
    labels: priceLevelsData.map(p => p.label),
    datasets: [{
      label: 'Shares Affordable',
      data: priceLevelsData.map(p => p.sharesAffordable),
      backgroundColor: priceLevelsData.map(p => 
        p.type.includes('support') ? '#E53E3E' : 
        p.type.includes('resistance') ? '#28A745' : '#FFD93D'
      ),
      borderColor: priceLevelsData.map(p => 
        p.type.includes('support') ? '#E53E3E' : 
        p.type.includes('resistance') ? '#28A745' : '#FFD93D'
      ),
      borderWidth: 2
    }]
  } : null;

  const constraintChartData = result ? {
    labels: result.calculationDetails.map(c => c.factor),
    datasets: [{
      label: 'Maximum Shares',
      data: result.calculationDetails.map(c => c.maxShares),
      backgroundColor: "#19692C",
      borderColor: "#19692C",
      borderWidth: 2
    }]
  } : null;

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Maximum Buy Calculator</h1>
        </div>
        <p>Calculate maximum affordable shares, optimize position sizing, and manage buying constraints</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className="profit positive">
                        {calc.maxShares} shares
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Max Buy Calc'}
              </button>
              <button 
                className={`tab ${activeTab === "constraints" ? "active" : ""}`}
                onClick={() => setActiveTab("constraints")}
              >
                <i className="fas fa-sliders-h"></i>
                {isMobile ? 'Constraints' : 'Constraints'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Strategy Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Calculation Mode Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${calculationMode === "budget" ? "active" : ""}`}
                onClick={() => setCalculationMode("budget")}
              >
                <i className="fas fa-money-bill-wave"></i>
                Budget-Based
              </button>
              <button 
                className={`mode-btn ${calculationMode === "risk" ? "active" : ""}`}
                onClick={() => setCalculationMode("risk")}
              >
                <i className="fas fa-shield-alt"></i>
                Risk-Based
              </button>
              <button 
                className={`mode-btn ${calculationMode === "margin" ? "active" : ""}`}
                onClick={() => setCalculationMode("margin")}
              >
                <i className="fas fa-chess-board"></i>
                Margin-Based
              </button>
            </div>
          </div>

          {/* Max Buy Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  {/* Stock Details */}
                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Stock Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Stock Price</label>
                      <input 
                        type="number" 
                        name="stockPrice" 
                        value={inputs.stockPrice} 
                        onChange={handleChange}
                        placeholder="50.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-down"></i> Stop Loss Price</label>
                      <input 
                        type="number" 
                        name="stopLossPrice" 
                        value={inputs.stopLossPrice} 
                        onChange={handleChange}
                        placeholder="45.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {/* Budget-Based Inputs */}
                  {(calculationMode === "budget" || constraints.find(c => c.type === "budget" && c.active)) && (
                    <>
                      <h3>
                        <i className="fas fa-wallet"></i>
                        Budget Constraints
                      </h3>
                      <div className="input-row">
                        <div className="input-field">
                          <label><i className="fa-solid fa-piggy-bank"></i> Available Cash</label>
                          <input 
                            type="number" 
                            name="availableCash" 
                            value={inputs.availableCash} 
                            onChange={handleChange}
                            placeholder="5000"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-percentage"></i> Max Budget %</label>
                          <input 
                            type="number" 
                            name="maxBudgetPercent" 
                            value={inputs.maxBudgetPercent} 
                            onChange={handleChange}
                            placeholder="100"
                            step="1"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-bolt"></i> Daily Buying Power</label>
                          <input 
                            type="number" 
                            name="dailyBuyingPower" 
                            value={inputs.dailyBuyingPower} 
                            onChange={handleChange}
                            placeholder="10000"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Risk-Based Inputs */}
                  {(calculationMode === "risk" || constraints.find(c => c.type === "risk" && c.active)) && (
                    <>
                      <h3>
                        <i className="fas fa-shield-alt"></i>
                        Risk Management
                      </h3>
                      <div className="input-row">
                        <div className="input-field">
                          <label><i className="fa-solid fa-exclamation-triangle"></i> Risk Capital</label>
                          <input 
                            type="number" 
                            name="riskCapital" 
                            value={inputs.riskCapital} 
                            onChange={handleChange}
                            placeholder="10000"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-percentage"></i> Max Risk %</label>
                          <input 
                            type="number" 
                            name="maxRiskPercent" 
                            value={inputs.maxRiskPercent} 
                            onChange={handleChange}
                            placeholder="2"
                            step="0.1"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-bolt"></i> Price Volatility %</label>
                          <input 
                            type="number" 
                            name="priceVolatility" 
                            value={inputs.priceVolatility} 
                            onChange={handleChange}
                            placeholder="15"
                            step="0.1"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Margin-Based Inputs */}
                  {(calculationMode === "margin" || constraints.find(c => c.type === "margin" && c.active)) && (
                    <>
                      <h3>
                        <i className="fas fa-chess-board"></i>
                        Margin Trading
                      </h3>
                      <div className="input-row">
                        <div className="input-field">
                          <label><i className="fa-solid fa-balance-scale"></i> Account Equity</label>
                          <input 
                            type="number" 
                            name="accountEquity" 
                            value={inputs.accountEquity} 
                            onChange={handleChange}
                            placeholder="25000"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-percentage"></i> Margin Requirement %</label>
                          <input 
                            type="number" 
                            name="marginRequirement" 
                            value={inputs.marginRequirement} 
                            onChange={handleChange}
                            placeholder="50"
                            step="1"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-chart-bar"></i> Current Margin Used</label>
                          <input 
                            type="number" 
                            name="currentMarginUsed" 
                            value={inputs.currentMarginUsed} 
                            onChange={handleChange}
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Portfolio Constraints */}
                  <h3>
                    <i className="fas fa-pie-chart"></i>
                    Portfolio Limits
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-piggy-bank"></i> Portfolio Value</label>
                      <input 
                        type="number" 
                        name="portfolioValue" 
                        value={inputs.portfolioValue} 
                        onChange={handleChange}
                        placeholder="50000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Max Position %</label>
                      <input 
                        type="number" 
                        name="maxPositionPercent" 
                        value={inputs.maxPositionPercent} 
                        onChange={handleChange}
                        placeholder="20"
                        step="1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-industry"></i> Max Sector Exposure %</label>
                      <input 
                        type="number" 
                        name="sectorExposure" 
                        value={inputs.sectorExposure} 
                        onChange={handleChange}
                        placeholder="15"
                        step="1"
                      />
                    </div>
                  </div>

                  {/* Costs & Fees */}
                  <h3>
                    <i className="fas fa-receipt"></i>
                    Costs & Fees
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Commission</label>
                      <input 
                        type="number" 
                        name="commission" 
                        value={inputs.commission} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Other Fees</label>
                      <input 
                        type="number" 
                        name="fees" 
                        value={inputs.fees} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Estimated Taxes %</label>
                      <input 
                        type="number" 
                        name="taxes" 
                        value={inputs.taxes} 
                        onChange={handleChange}
                        placeholder="15"
                        step="0.1"
                      />
                    </div>
                  </div>

                  {/* Advanced Settings */}
                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Advanced Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-rocket"></i> Leverage</label>
                      <select name="leverage" value={inputs.leverage} onChange={handleChange}>
                        <option value="1">1x (No Leverage)</option>
                        <option value="2">2x</option>
                        <option value="3">3x</option>
                        <option value="5">5x</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Market Condition</label>
                      <select name="marketCondition" value={inputs.marketCondition} onChange={handleChange}>
                        <option value="bullish">Bullish</option>
                        <option value="normal">Normal</option>
                        <option value="bearish">Bearish</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-globe"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currency" 
                        value={inputs.currency} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Constraints Tab */}
          {activeTab === "constraints" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-sliders-h"></i>
                  Buying Constraints
                </h3>
                
                {constraints.map((constraint, index) => (
                  <div key={index} className="constraint-group">
                    <div className="constraint-header">
                      <div className="constraint-toggle">
                        <input 
                          type="checkbox"
                          checked={constraint.active}
                          onChange={() => toggleConstraint(index)}
                          className="checkbox-input"
                        />
                        <label className="checkbox-label">
                          {constraint.type.toUpperCase()} Constraint
                        </label>
                      </div>
                      {constraints.length > 1 && (
                        <button 
                          className="remove-constraint-btn"
                          onClick={() => removeConstraint(index)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                    <div className="input-row">
                      <div className="input-field">
                        <label><i className="fa-solid fa-dollar-sign"></i> Maximum Value</label>
                        <input 
                          type="number" 
                          value={constraint.value}
                          onChange={(e) => handleConstraintChange(index, 'value', e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button className="btn add-purchase-btn" onClick={addConstraint}>
                  <i className="fas fa-plus"></i>
                  Add Another Constraint
                </button>

                <div className="constraints-info">
                  <h4>About Constraints</h4>
                  <div className="info-list">
                    <div className="info-item">
                      <i className="fas fa-info-circle"></i>
                      <span><strong>Budget Constraint:</strong> Limits based on available cash</span>
                    </div>
                    <div className="info-item">
                      <i className="fas fa-info-circle"></i>
                      <span><strong>Risk Constraint:</strong> Limits based on risk management rules</span>
                    </div>
                    <div className="info-item">
                      <i className="fas fa-info-circle"></i>
                      <span><strong>Portfolio Constraint:</strong> Limits position size as % of portfolio</span>
                    </div>
                    <div className="info-item">
                      <i className="fas fa-info-circle"></i>
                      <span><strong>Margin Constraint:</strong> Limits based on margin availability</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit positive">
                          <i className="fas fa-shopping-cart"></i>
                          {calc.maxShares} shares
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-dollar-sign"></i> Investment: ${calc.maxInvestment?.toFixed(2)}</span>
                        <span><i className="fas fa-chart-pie"></i> Size: {calc.positionSizePercent?.toFixed(2)}%</span>
                        <span><i className="fas fa-filter"></i> Limit: {calc.limitingFactor}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "constraints") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-shopping-cart"></i>
                    Maximum Buy Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value positive">
                          {result.maxShares} shares
                        </div>
                        <div className="result-label">
                          <i className="fas fa-cube"></i>
                          Maximum Shares
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.leveragedShares} shares
                        </div>
                        <div className="result-label">
                          <i className="fas fa-rocket"></i>
                          With Leverage
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.totalInvestment.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          Total Investment
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.positionSizePercent.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-pie"></i>
                          Portfolio Size
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-dollar-sign"></i> Stock Price:</span>
                        <span>${result.stockPrice.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-receipt"></i> Stock Cost:</span>
                        <span>${result.totalCost.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-coins"></i> Fees & Commission:</span>
                        <span>${result.totalFees.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-percentage"></i> Estimated Taxes:</span>
                        <span>${result.estimatedTaxes.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-filter"></i> Limiting Factor:</span>
                        <span>{result.limitingFactor}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-bolt"></i> Leverage:</span>
                        <span>{result.leverage}x</span>
                      </div>
                    </div>

                    {/* Capacity Analysis */}
                    <div className="risk-summary">
                      <h4>Capacity Analysis</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.remainingCash.toFixed(2)}
                          </div>
                          <div className="risk-label">Remaining Cash</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            {result.cashUtilization.toFixed(2)}%
                          </div>
                          <div className="risk-label">Cash Utilization</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.remainingBuyingPower.toFixed(2)}
                          </div>
                          <div className="risk-label">Remaining Buying Power</div>
                        </div>
                      </div>
                    </div>

                    {/* Constraints Summary */}
                    <div className="risk-summary">
                      <h4>Constraints Applied</h4>
                      <div className="constraints-list">
                        {result.calculationDetails.map((calc, index) => (
                          <div key={index} className="constraint-item">
                            <div className="constraint-name">
                              <i className="fas fa-filter"></i>
                              {calc.factor}
                            </div>
                            <div className="constraint-value">
                              {calc.maxShares} shares (${calc.maxInvestment.toFixed(2)})
                            </div><br/>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Investment Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Investment Cost Breakdown
                      </h3>
                      <Doughnut 
                        data={investmentChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Constraints Comparison Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-bar"></i>
                        Constraints Comparison
                      </h3>
                      <Bar 
                        data={constraintChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: false
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Scenario Analysis Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chess-board"></i>
                        Scenario Analysis
                      </h3>
                      <Bar 
                        data={scenarioChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top'
                            }
                          },
                          scales: {
                            y: {
                              title: {
                                display: true,
                                text: 'Profit/Loss ($)'
                              }
                            },
                            y1: {
                              position: 'right',
                              title: {
                                display: true,
                                text: 'Expected Value ($)'
                              },
                              grid: {
                                drawOnChartArea: false
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Price Levels Analysis */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-mountain"></i>
                        Price Levels Analysis
                      </h3>
                      <Bar 
                        data={priceLevelsChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: false
                            }
                          },
                          scales: {
                            y: {
                              title: {
                                display: true,
                                text: 'Shares Affordable'
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Detailed Scenario Analysis */}
                    <div className="risk-summary">
                      <h4>Detailed Scenario Analysis</h4>
                      <div className="scenario-list">
                        {scenarioData.map((scenario, index) => (
                          <div key={index} className={`scenario-item ${scenario.isProfit ? 'positive' : 'negative'}`}>
                            <div className="scenario-header">
                              <span className="scenario-name">{scenario.label}</span>
                              <span className="scenario-price">${scenario.newPrice.toFixed(2)}</span>
                            </div>
                            <div className="scenario-details">
                              <span>P/L: ${scenario.profitLoss.toFixed(2)}</span>
                              <span>Return: {scenario.percentChange.toFixed(2)}%</span>
                              <span>Probability: {scenario.probability}%</span>
                              <span>Expected: ${scenario.expectedValue.toFixed(2)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-shopping-cart fa-3x"></i>
                    <p>Enter your parameters to calculate maximum affordable shares</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "constraints" && result && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-filter"></i>
                  Constraints Analysis
                </h3>
                
                <div className="constraints-overview">
                  <div className="overview-card">
                    <div className="overview-value">{result.activeConstraints}</div>
                    <div className="overview-label">Active Constraints</div>
                  </div>
                  <div className="overview-card">
                    <div className="overview-value">{result.appliedConstraints}</div>
                    <div className="overview-label">Applied Constraints</div>
                  </div>
                  <div className="overview-card">
                    <div className="overview-value">{result.limitingFactor}</div>
                    <div className="overview-label">Limiting Factor</div>
                  </div>
                </div>

                <div className="constraints-details">
                  <h4>Constraint Details</h4>
                  {result.calculationDetails.map((calc, index) => (
                    <div key={index} className="constraint-detail">
                      <div className="constraint-header">
                        <span className="constraint-type">{calc.type.toUpperCase()}</span>
                        <span className={`constraint-status ${calc.maxShares === result.maxShares ? 'limiting' : 'non-limiting'}`}>
                          {calc.maxShares === result.maxShares ? 'LIMITING' : 'NON-LIMITING'}
                        </span>
                      </div>
                      <div className="constraint-metrics">
                        <div className="metric">
                          <span className="metric-label">Maximum Shares:</span>
                          <span className="metric-value">{calc.maxShares}</span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Investment Limit:</span>
                          <span className="metric-value">${calc.limit.toFixed(2)}</span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Actual Investment:</span>
                          <span className="metric-value">${calc.maxInvestment.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="optimization-tips">
                  <h4>Optimization Tips</h4>
                  <div className="tips-list">
                    {result.remainingCash > 0 && (
                      <div className="tip-item positive">
                        <i className="fas fa-lightbulb"></i>
                        <span>You have ${result.remainingCash.toFixed(2)} remaining cash available</span>
                      </div>
                    )}
                    {result.cashUtilization < 80 && (
                      <div className="tip-item positive">
                        <i className="fas fa-lightbulb"></i>
                        <span>Only {result.cashUtilization.toFixed(2)}% of cash utilized - consider increasing position</span>
                      </div>
                    )}
                    {result.positionSizePercent < 10 && (
                      <div className="tip-item positive">
                        <i className="fas fa-lightbulb"></i>
                        <span>Position size is only {result.positionSizePercent.toFixed(2)}% of portfolio - well diversified</span>
                      </div>
                    )}
                    {result.leverage > 1 && (
                      <div className="tip-item warning">
                        <i className="fas fa-exclamation-triangle"></i>
                        <span>Leverage of {result.leverage}x increases risk - ensure proper risk management</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default MaxBuyCalculator;